import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button } from "@sunwing/wvi-components";
import { LinkConnector } from "../LinkConnector";
import { FluidSections } from "../FluidSections";
import { ModalConnector } from "../ModalConnector";
import * as styles from "./LinkButton.module.scss";
import { RCL as useTranslation } from "../../components/RCL";

const linkAdapter = link => {
	if (!link?.variant) {
		return link ?? {};
	}
	let variant = link.variant;
	switch (variant) {
		case "Primary (Outline)":
			variant = "outline";
			break;
		case "Primary (Inverse)":
			variant = "inverse";
			break;
		case "Action":
			variant = "action";
			break;
		case "Action (Outline)":
			variant = "actionOutline";
			break;
		case "Action (Inverse)":
			variant = "actionInverse";
			break;
		case "Action (Inverse Filled)":
			variant = "actionInverseFilled";
			break;
		case "Utility":
			variant = "utility";
			break;
		case "IconOnly":
			variant = "iconOnly";
			break;
		case "NoStyle":
			variant = "noStyle";
			break;
		case "Primary":
		default:
			variant = "primary";
			break;
	}

	const sizeMap = {
		Small: "sm",
		Medium: "md",
		Large: "lg",
		"X-Large": "xl",
	};

	let linkSize =
		link.type === "link" && link.linkSize === "X-Large"
			? "lg"
			: sizeMap[link.linkSize] || (link.type === "link" ? "lg" : "md");

	const placementMap = {
		Left: "left",
		Right: "right",
	};

	let iconPlacement =
		link.iconPlacement in placementMap ? placementMap[link.iconPlacement] : link.iconPlacement;

	const themeMap = {
		Dark: "dark",
		Light: "light",
	};

	let theme = link.theme in themeMap ? themeMap[link.theme] : link.theme || "light";

	return {
		...link,
		variant,
		linkSize,
		iconPlacement,
		theme,
	};
};

const LinkButtonConnector = ({ data, onLinkModalOpen, ...props }) => {
	const [isOpen, setIsOpen] = useState(false);
	const labels = {
		ok: useTranslation({
			searchKey: "ok",
		}),
		cancel: useTranslation({
			searchKey: "cancel-vwq",
		}),
	};

	const link = linkAdapter(data);

	const handleClose = () => {
		setIsOpen(false);
	};

	return (
		<>
			{(link.modal || link.externalModalMessage) && (
				<ModalConnector
					isOpen={isOpen}
					handleClose={handleClose}
					isDismissible={link.target !== "EXTERNAL_MODAL"}>
					{/* if externalModalMessage is true render the messages TheN FLUID SECTION */}
					{link.externalModalMessage ? (
						<p>{data.externalModalMessage}</p>
					) : (
						<FluidSections data={link.modal} />
					)}
					{link.target === "EXTERNAL_MODAL" && (
						<>
							<Button
								className={styles?.modalOkBtn}
								onClick={() => {
									handleClose();
									window.open(link.url, "_blank").focus();
								}}
								variant="action"
								size={link.linkSize}>
								{labels.ok}
							</Button>
							<Button
								className={styles?.modalCancelBtn}
								onClick={handleClose}
								variant="actionOutline"
								size={link.linkSize}>
								{labels.cancel}
							</Button>
						</>
					)}
				</ModalConnector>
			)}

			{link.type === "button" ? (
				<Button
					as={link.modal ? "button" : "a"}
					icon={link.iconName}
					size={link.linkSize}
					href={link.url}
					variant={link.variant}
					iconPlacement={link.iconPlacement}
					target={link.target !== "EXTERNAL_MODAL" ? link.target : undefined}
					type={link.type}
					// eslint-disable-next-line react/jsx-props-no-spreading
					{...(link.modal
						? {
								onClick: e => {
									e.preventDefault();
									onLinkModalOpen?.();

									setTimeout(() => {
										setIsOpen(true);
									}, 0);
								},
							}
						: {})}
					renderLink={(href, { children, ...linkProps }) => (
						// eslint-disable-next-line react/jsx-props-no-spreading
						<LinkConnector to={href} noStyle={true} {...linkProps}>
							{children}
						</LinkConnector>
					)}
					// eslint-disable-next-line react/jsx-props-no-spreading
					{...props}>
					{link.linkText}
				</Button>
			) : (
				<LinkConnector
					image={link.image}
					imageAltText={link.imageAltText}
					to={link.url}
					target={link.target !== "EXTERNAL_MODAL" ? link.target : undefined}
					icon={link.iconName}
					size={link.linkSize}
					theme={link.theme}
					// eslint-disable-next-line react/jsx-props-no-spreading
					{...(link.modal || link.externalModalMessage
						? {
								onClick: e => {
									e.preventDefault();
									onLinkModalOpen?.();

									setTimeout(() => {
										setIsOpen(true);
									}, 0);
								},
							}
						: {})}
					// eslint-disable-next-line react/jsx-props-no-spreading
					{...props}>
					{link.linkText}
				</LinkConnector>
			)}
		</>
	);
};

LinkButtonConnector.propTypes = {
	data: PropTypes.shape({
		image: PropTypes.arrayOf(
			PropTypes.shape({
				secure_url: PropTypes.string,
				width: PropTypes.number,
				height: PropTypes.number,
				raw_transformation: PropTypes.string,
				version: PropTypes.number,
				public_id: PropTypes.string,
				format: PropTypes.string,
			})
		),
		imageAltText: PropTypes.string,
		iconName: PropTypes.string,
		iconPlacement: PropTypes.string,
		link: PropTypes.string,
		linkText: PropTypes.node,
		linkSize: PropTypes.string,
		theme: PropTypes.oneOf(["Light", "Dark"]),
		target: PropTypes.string,
		type: PropTypes.string,
		url: PropTypes.string.isRequired,
		variant: PropTypes.oneOf([
			"Primary",
			"Primary (Outline)",
			"Primary (Inverse)",
			"Action",
			"Action (Outline)",
			"Action (Inverse)",
			"Action (Inverse Filled)",
			"Utility",
			"IconOnly",
			"NoStyle",
		]).isRequired,
		align: PropTypes.oneOf(["left", "right", "center"]),
		modal: PropTypes.array,
		externalModalMessage: PropTypes.string,
	}).isRequired,
	onLinkModalOpen: PropTypes.func,
};

LinkButtonConnector.defaultProps = {
	onLinkModalOpen: undefined,
};

export { LinkButtonConnector, linkAdapter };
